import { Link } from 'react-router-dom';
import { Lock, Plus, Sparkles, X } from 'lucide-react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Skeleton,
  Switch,
  Textarea,
  cn,
} from '@design-system';
import type { EmailTestResult } from '../api/api';
import { reasonInWords } from '../conversationWords';
import { useEmailTest } from '../hooks/use-email-test';
import { EmailFrame } from './EmailFrame';
import { ProbabilityBars } from './ProbabilityBars';
import { replyPath } from './ReplyLibrary';
import { TeachReply } from './TeachReply';

const MAILBOX = 'magic@support.joinwaterlily.com';

const CLIENT_WORDS: Record<EmailTestResult['client']['status'], string> = {
  resolved: '',
  ambiguous: 'The name matched more than one client, so the reply asks which one.',
  not_found: 'No client of this advisor matched the name.',
  none: 'The email does not name a client.',
  not_read: '',
};

const VIA_WORDS: Record<string, string> = {
  name: 'from the name in the email',
  earlier: 'the client from earlier in the thread',
  list_number: 'picked by number from an earlier list',
};

function Outcome({ result }: { result: EmailTestResult }) {
  const answered = result.outcome === 'reply' && result.reply;
  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-xl px-4 py-3',
        answered ? 'bg-[#E7F6EC] text-[#1B6B3A]' : 'bg-[#FFF4E5] text-[#8A4B00]',
      )}
    >
      <span className="text-[15px] font-semibold">
        {answered ? `Answered with “${result.reply!.title}”` : 'Goes to our team'}
        {answered && result.reply!.status !== 'live' && ' (if it were published)'}
      </span>
      <span className="text-[13px]">
        {answered
          ? `${result.competing} ${result.competing === 1 ? 'reply' : 'replies'} competed.`
          : `Why: ${reasonInWords(result.reason)}.`}
      </span>
    </div>
  );
}

/**
 * Try an email as an advisor would send it: which reply answers it and how sure the choice was,
 * the client it was read as being about, the email that would go out, and the AI's draft; and teach
 * a reply the email when the choice was wrong. Nothing is sent or recorded by trying.
 */
export function EmailTest() {
  const test = useEmailTest();
  const { result } = test;

  return (
    <div className="flex flex-wrap items-start gap-6">
      <section className="flex min-w-0 flex-[1_1_380px] flex-col gap-4 rounded-xl border border-zinc-200 bg-white px-5 py-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">Try an email</h2>
          <p className="text-[13px] text-zinc-600">
            See what an advisor would get back. Nothing is sent, and the advisor never sees it.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="test-advisor">As the advisor</Label>
          <Input
            id="test-advisor"
            className="h-11"
            placeholder="Their email, or empty to test as you"
            value={test.form.advisorEmail}
            onChange={event => test.setAdvisorEmail(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="test-subject">Subject</Label>
          <Input
            id="test-subject"
            className="h-11"
            placeholder="Optional"
            value={test.form.subject}
            onChange={event => test.setSubject(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="test-email">Their email</Label>
          <Textarea
            id="test-email"
            rows={6}
            placeholder="e.g. Has Maria Snith finished her intake yet?"
            value={test.form.email}
            onChange={event => test.setEmail(event.target.value)}
          />
        </div>

        <Collapsible className="rounded-lg border border-zinc-200 px-3">
          <CollapsibleTrigger className="flex min-h-11 w-full items-center justify-between text-sm font-medium">
            It continues a conversation
            <span className="text-[13px] font-normal text-zinc-500">
              {test.history.length > 0 ? `${test.history.length} earlier` : 'Optional'}
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-2 pb-3">
            {test.history.map((turn, index) => (
              <div
                key={index}
                className="flex items-start gap-2"
              >
                <span className="mt-2.5 w-20 shrink-0 text-[12px] font-semibold text-zinc-600">
                  {turn.role === 'advisor' ? 'Advisor' : 'Waterlily'}
                </span>
                <Textarea
                  rows={2}
                  aria-label={`Earlier message ${index + 1}`}
                  value={turn.content}
                  onChange={event => test.setTurn(index, event.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove earlier message ${index + 1}`}
                  onClick={() => test.removeTurn(index)}
                >
                  <X aria-hidden />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => test.addTurn('advisor')}
              >
                <Plus aria-hidden /> Advisor wrote
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => test.addTurn('assistant')}
              >
                <Plus aria-hidden /> Waterlily replied
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <label className="flex min-h-11 items-center justify-between gap-3 text-sm">
          <span>
            Include drafts
            <span className="block text-[13px] text-zinc-600">As if every reply ready to publish were live</span>
          </span>
          <Switch
            checked={test.form.includeDrafts}
            onCheckedChange={test.setIncludeDrafts}
          />
        </label>

        <Button
          className="h-11 font-semibold"
          disabled={!test.canTry}
          onClick={test.tryIt}
        >
          {test.trying ? 'Trying…' : 'Try it'}
        </Button>
      </section>

      <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-4">
        {test.trying && !result ? (
          <Skeleton className="h-96 w-full" />
        ) : !result ? (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-10 text-center text-sm text-zinc-600">
            Write an email and press Try it. You’ll see how sure the assistant is, which reply it picks, and the
            email the advisor would get.
          </div>
        ) : (
          <>
            <Outcome result={result} />
            {test.draftOf && (
              <p className="rounded-xl border border-[#D9D0FB] bg-[#FBFAFF] px-4 py-3 text-[13px] text-zinc-700">
                Tried again with the new example, as a draft of its reply. It isn’t live until it is published:{' '}
                <Link
                  to={replyPath(test.draftOf)}
                  className="font-medium text-[#4527B8]"
                >
                  open the reply to publish it
                </Link>
                .
              </p>
            )}

            <section className="flex flex-col gap-5 rounded-xl border border-zinc-200 bg-white px-5 py-5">
              <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold">How it was chosen</h2>
                <p className="text-[13px] text-zinc-600">
                  First the kind of request, then a reply of that kind. Each must be at least 60% likely and 15 points
                  ahead of the next, or a person answers.
                </p>
              </div>
              <ProbabilityBars
                title="Kind of request"
                step={result.selection.category}
                empty="Not asked."
              />
              <ProbabilityBars
                title="Reply"
                step={result.selection.template}
                empty="Not asked: the kind of request was not clear enough."
              />
              {result.selection.severalRequests !== null && result.selection.severalRequests >= 0.5 && (
                <p className="text-[13px] text-[#8A4B00]">
                  The email looks like it asks several things at once (
                  {Math.round(result.selection.severalRequests * 100)}%).
                </p>
              )}
            </section>

            <TeachReply
              key={test.runId}
              result={result}
              email={test.form.email}
              tryAgainWith={test.tryAgainWith}
            />

            <section className="flex flex-col gap-2 rounded-xl border border-zinc-200 bg-white px-5 py-4">
              <h2 className="text-base font-semibold">Client</h2>
              <p className="text-sm text-zinc-700">
                {result.client.status === 'resolved' && result.client.name
                  ? `${result.client.name}, ${VIA_WORDS[result.client.via ?? ''] ?? 'found'}.`
                  : CLIENT_WORDS[result.client.status]}
              </p>
            </section>

            {result.email && (
              <section className="flex flex-col gap-3 rounded-xl border border-zinc-200 bg-white px-6 py-5">
                <h2 className="text-base font-semibold">What {result.advisor.name ?? result.advisor.email} would get</h2>
                <EmailFrame
                  title="The email that would be sent"
                  subject={result.email.subject}
                  from={MAILBOX}
                  html={result.email.html}
                />
              </section>
            )}

            <section className="flex flex-col gap-3 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold">AI draft · never sent</h2>
                {!test.draft.data && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={test.draft.isPending}
                    onClick={test.writeDraft}
                  >
                    <Sparkles aria-hidden /> {test.draft.isPending ? 'Writing… up to 25 seconds' : 'Write the AI draft'}
                  </Button>
                )}
              </div>
              {test.draft.data?.locked ? (
                <p className="flex items-center gap-2 text-sm text-zinc-600">
                  <Lock
                    className="size-4"
                    aria-hidden
                  />
                  Only reviewers can read AI drafts.
                </p>
              ) : test.draft.data ? (
                <p className="whitespace-pre-wrap text-sm leading-[1.55] text-zinc-700">{test.draft.data.text}</p>
              ) : (
                <p className="text-[13px] text-zinc-600">
                  What the old AI assistant would have written. Kept for comparison only.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
